import { ConstantsUtil, NetworkUtil } from '@reown/appkit-common';
import { ChainController, CoreHelperUtil, getActiveCaipNetwork } from '@reown/appkit-controllers';
import { HelpersUtil } from '@reown/appkit-utils';
const subscriptions = [];
export function mapToSIWX(siwe) {
    async function getSession() {
        try {
            const response = await siwe.methods.getSession();
            if (!response) {
                return undefined;
            }
            if (!response?.address) {
                throw new Error('SIWE session is missing address');
            }
            if (!response?.chainId) {
                throw new Error('SIWE session is missing chainId');
            }
            return response;
        }
        catch (error) {
            console.warn('AppKit:SIWE:getSession - error:', error);
            return undefined;
        }
    }
    let signingOut = undefined;
    async function signOut() {
        if (signingOut) {
            return signingOut;
        }
        signingOut = (async () => {
            try {
                await siwe.methods.signOut();
                siwe.methods.onSignOut?.();
            }
            finally {
                signingOut = undefined;
            }
        })();
        return signingOut;
    }
    subscriptions.forEach(unsubscribe => unsubscribe());
    subscriptions.push(ChainController.subscribeKey('activeCaipAddress', async (activeCaipAddress) => {
        if (siwe.options.signOutOnDisconnect && !activeCaipAddress) {
            const session = await getSession();
            if (session) {
                await signOut();
            }
            return;
        }
        if (activeCaipAddress) {
            const session = await getSession();
            if (session && siwe.options.signOutOnAccountChange) {
                const sessionAddress = session?.address;
                const caipAddress = CoreHelperUtil?.getPlainAddress(activeCaipAddress);
                const isDifferentAddress = !HelpersUtil.isLowerCaseMatch(sessionAddress, caipAddress);
                if (isDifferentAddress) {
                    await signOut();
                }
            }
        }
    }));
    return {
        async createMessage(input) {
            const params = await siwe.methods.getMessageParams?.();
            if (!params) {
                throw new Error('Failed to get message params!');
            }
            const nonce = await siwe.getNonce(input.accountAddress);
            const issuedAt = params.iat || new Date().toISOString();
            const version = '1';
            return {
                nonce,
                version,
                requestId: params.requestId,
                accountAddress: input.accountAddress,
                chainId: input.chainId,
                domain: params.domain,
                uri: params.uri,
                notBefore: params.nbf,
                resources: params.resources,
                statement: params.statement,
                expirationTime: params.exp,
                issuedAt,
                toString: () => siwe.createMessage({
                    ...params,
                    chainId: NetworkUtil.caipNetworkIdToNumber(input.chainId) || 1,
                    address: `did:pkh:${input.chainId}:${input.accountAddress}`,
                    nonce,
                    version,
                    iat: issuedAt
                })
            };
        },
        async addSession(session) {
            const chainId = NetworkUtil.parseEvmChainId(session.data.chainId);
            if (!chainId) {
                return Promise.resolve();
            }
            if (await siwe.methods.verifyMessage(session)) {
                const address = session.data.accountAddress;
                const network = NetworkUtil.parseEvmChainId(session.data.chainId);
                siwe.methods.onSignIn?.({
                    address,
                    chainId: network
                });
                return Promise.resolve();
            }
            throw new Error('Failed to verify message');
        },
        async revokeSession(_chainId, _address) {
            try {
                await signOut();
            }
            catch (error) {
                console.warn('AppKit:SIWE:revokeSession - signOut error', error);
            }
        },
        async setSessions(sessions) {
            if (sessions.length === 0) {
                try {
                    await signOut();
                }
                catch (error) {
                    console.warn('AppKit:SIWE:setSessions - signOut error', error);
                }
            }
            else {
                const session = (sessions.find(s => s.data.chainId === getActiveCaipNetwork()?.caipNetworkId) || sessions[0]);
                await this.addSession(session);
            }
        },
        async getSessions(chainId, address) {
            try {
                if (!chainId.startsWith('eip155:')) {
                    return [
                        {
                            data: {
                                accountAddress: address,
                                chainId
                            },
                            message: '',
                            signature: ''
                        }
                    ];
                }
                const siweSession = await getSession();
                const siweCaipNetworkId = `${ConstantsUtil.CHAIN.EVM}:${siweSession?.chainId}`;
                const sessionAddress = siweSession?.address;
                const requestedCaipNetworkId = chainId;
                const shouldSignOutOnNetworkChange = siwe.options.signOutOnNetworkChange;
                const shouldSignOutOnAccountChange = siwe.options.signOutOnAccountChange;
                const isNetworkMismatch = shouldSignOutOnNetworkChange && siweCaipNetworkId !== requestedCaipNetworkId;
                const isAddressMismatch = shouldSignOutOnAccountChange && !HelpersUtil.isLowerCaseMatch(sessionAddress, address);
                const shouldSignOut = isNetworkMismatch || isAddressMismatch;
                if (!siweSession || shouldSignOut) {
                    return [];
                }
                const session = {
                    data: {
                        accountAddress: siweSession.address,
                        chainId: siweCaipNetworkId
                    },
                    message: '',
                    signature: ''
                };
                return [session];
            }
            catch (error) {
                console.warn('AppKit:SIWE:getSessions - error:', error);
                return [];
            }
        },
        getRequired() {
            return siwe.options.required ?? true;
        },
        signOutOnDisconnect: siwe.options.signOutOnDisconnect
    };
}
//# sourceMappingURL=mapToSIWX.js.map